import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useAuthStore } from '@/features/auth/auth.store';
import { useCompanyStore } from '@/stores/company.store';

import type { FeatureFlagKey } from './feature-flag.constants';
import { FeatureFlagService } from './feature-flag.services';

/**
 * The company the flags are read for — the same pick the booking instance
 * scopes requests to: the switcher first, then the account's own company
 * while the switcher store is still rehydrating.
 */
const useScopeCompanyId = () => {
  const selected = useCompanyStore(state => state.company?.id);
  const own = useAuthStore(state => state.user?.company?.id);
  return selected || own || undefined;
};

/**
 * Release flags for the company in scope.
 *
 * Fails closed: while loading, on an error, or for a key the API did not
 * return, a flag reads as off, so the client keeps the flow that is known to
 * work. Keyed by company, so switching tenant reads that tenant's flags; the
 * Console's save invalidates the `feature-flags` root and refreshes these too.
 */
const useFeatureFlags = () => {
  const companyId = useScopeCompanyId();

  const { data, isPending, isError } = useQuery({
    queryKey: ['feature-flags', 'me', companyId],
    queryFn: FeatureFlagService.getMyFlags,
    select: response => response.data ?? {},
    enabled: !!companyId,
    // A switched-off feature should leave open tabs within minutes.
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const isEnabled = useCallback(
    (key?: FeatureFlagKey) => (key ? data?.[key] === true : true),
    [data]
  );

  return {
    flags: data ?? {},
    // "No flags yet", not "fetching": a tenant switch cancels the new
    // tenant's first request before refetching it, and in between the query
    // sits idle with no data. Reading that as loaded would flash every flag
    // off. Without a company there is nothing to load; the flags read off.
    isLoading: !!companyId && isPending,
    isError,
    isEnabled,
  };
};

/** One flag: `enabled` is false while loading, so callers can wait first. */
const useFeature = (key: FeatureFlagKey) => {
  const { isEnabled, isLoading } = useFeatureFlags();
  return { enabled: isEnabled(key), isLoading };
};

export { useFeatureFlags, useFeature };
